import { clsx } from 'clsx'
import * as React from 'react'
import {
	Link,
	useFetcher,
	useLocation,
	useSearchParams,
	data as json,
	type HeadersFunction,
	type MetaFunction,
} from 'react-router'
import { ButtonLink } from '#app/components/button.tsx'
import { Grid } from '#app/components/grid.tsx'
import { YoutubeIcon } from '#app/components/icons.tsx'
import { CourseSection } from '#app/components/sections/course-section.tsx'
import { HeroSection } from '#app/components/sections/hero-section.tsx'
import { Tag } from '#app/components/tag.tsx'
import { H3, H4, H6, Paragraph } from '#app/components/typography.tsx'
import { images } from '#app/images.tsx'
import {
	FavoriteToggle,
	favoriteResourceRoute,
} from '#app/routes/resources/favorite.tsx'
import {
	formatDate,
	parseDate,
	reuseUsefulLoaderHeaders,
	useUpdateQueryStringValueWithoutNavigation,
} from '#app/utils/misc-react.tsx'
import { externalLinks } from '#app/external-links.tsx'
import { listify } from '#app/utils/misc.ts'
import { type SerializeFrom } from '#app/utils/serialize-from.ts'
import { getTalksAndTags } from '#app/utils/talks.server.ts'
import { useOptionalUser } from '#app/utils/use-root-data.ts'
import { type Route } from './+types/_layout'

export const meta: MetaFunction<typeof loader> = ({ data }) =>
	data?.socialMetas ?? []

export async function loader({ request }: Route.LoaderArgs) {
	const talksAndTags = await getTalksAndTags({ request })
	const { talks, tags } = talksAndTags
	const talkCount = talks.length
	const deliveryCount = talks.flatMap((t) => t.deliveries).length
	const title = `${talkCount} talks by Kent all about software development`
	const topicsList = listify(tags.slice(0, 6))
	const socialMetas = (
		await import('#app/og/page-meta.server.ts')
	).buildPageSocialMetasForRequest(request, {
		title,
		description: `Check out Kent's ${talkCount} talks he's delivered ${deliveryCount} times. Topics include: ${topicsList}`,
		socialImage: {
			kind: 'generic-social',
			words: title,
			featuredImage: images.teslaY.id,
		},
	})

	return json({ ...talksAndTags, socialMetas }, {
		headers: {
			'Cache-Control': 'public, max-age=3600',
			Vary: 'Cookie',
		},
	})
}

export const headers: HeadersFunction = reuseUsefulLoaderHeaders

function Card({
	tag,
	tags,
	title,
	slug,
	deliveries,
	descriptionHTML,
	resourceHTMLs,
	active,
	isFavorite,
}: SerializeFrom<typeof loader>['talks'][0] & {
	active: boolean
	isFavorite: boolean
}) {
	const latestDelivery = deliveries
		.filter((x) => x.date)
		.sort(
			(l, r) => parseDate(r.date!).getTime() - parseDate(l.date!).getTime(),
		)[0]

	const isInFuture = latestDelivery?.date
		? parseDate(latestDelivery.date).getTime() > Date.now()
		: true

	return (
		<div
			className={clsx(
				'relative flex h-full w-full flex-col rounded-lg bg-gray-100 p-6 md:p-16 dark:bg-gray-800',
				{
					'focus-ring ring-2': active,
				},
			)}
		>
			{/* place the scroll marker a bit above the element to act as view margin */}
			<div data-talk={slug} className="absolute -top-8" />

			<div className="mb-8 flex flex-none flex-col items-center justify-between md:flex-row">
				<div className="inline-flex items-baseline">
					{isInFuture ? (
						<div className="block h-3 w-3 flex-none rounded-full bg-green-600" />
					) : (
						<div className="block h-3 w-3 flex-none rounded-full bg-gray-400 dark:bg-gray-600" />
					)}
					{latestDelivery ? (
						<H6 as="p" className="pl-4">
							{latestDelivery.dateDisplay}
						</H6>
					) : null}
				</div>

				<div className="flex items-center gap-2 md:mt-0">
					{tag ? (
						<div className="inline-block self-start rounded-full bg-white px-8 py-4 text-lg whitespace-nowrap text-black dark:bg-gray-600 dark:text-white">
							{tag}
						</div>
					) : null}
					<FavoriteToggle
						mode="icon"
						contentType="talk"
						contentId={slug}
						initialIsFavorite={isFavorite}
					/>
				</div>
			</div>

			<Link to={`./${slug}`} className="mb-4 flex h-48 flex-none items-end">
				<H3 as="div">{title}</H3>
			</Link>

			<div className="mb-10 flex-auto">
				<Paragraph
					as="div"
					className="html mb-20"
					dangerouslySetInnerHTML={{ __html: descriptionHTML || '&nbsp;' }}
				/>

				{tags.length ? (
					<>
						<H6 as="div" className="mt-10 mb-2">
							Keywords
						</H6>
						<Paragraph className="flex">{tags.join(', ')}</Paragraph>
					</>
				) : null}

				{deliveries.length ? (
					<>
						<H6 as="div" className="mt-10 mb-2">
							Presentations
						</H6>
						<ul className="space-y-1">
							{deliveries.map((delivery, index) => (
								<li key={index}>
									<div className="flex w-full items-center gap-2">
										{delivery.date &&
										parseDate(delivery.date).getTime() > Date.now() ? (
											<div className="block h-2 w-2 flex-none animate-pulse rounded-full bg-green-600" />
										) : null}
										<Paragraph
											as="div"
											className="html"
											prose={true}
											dangerouslySetInnerHTML={{
												__html: delivery.eventHTML ?? '',
											}}
										/>

										{delivery.recording ? (
											<a
												className="text-secondary hover:text-team-current ml-2 flex-none"
												href={delivery.recording}
											>
												<YoutubeIcon size={32} />
											</a>
										) : null}

										<div className="flex-auto" />
										<Paragraph
											className="ml-2 flex-none tabular-nums"
											as="span"
										>
											{delivery.date
												? formatDate(delivery.date, 'yyyy-MM-dd')
												: null}
										</Paragraph>
									</div>
								</li>
							))}
						</ul>
					</>
				) : null}

				{resourceHTMLs.length ? (
					<>
						<H6 className="mt-10 mb-2" as="div">
							Resources
						</H6>
						<ul className="space-y-1">
							{resourceHTMLs.map((resource) => (
								<li key={resource}>
									<Paragraph
										as="div"
										className="html"
										prose={false}
										dangerouslySetInnerHTML={{ __html: resource }}
									/>
								</li>
							))}
						</ul>
					</>
				) : null}
			</div>
		</div>
	)
}

export default function TalksScreen({
	loaderData: data,
}: Route.ComponentProps) {
	const { pathname } = useLocation()
	const [activeSlug] = pathname.split('/').slice(-1)
	const user = useOptionalUser()
	const talkFavoritesFetcher = useFetcher<{ contentIds: Array<string> }>({
		key: 'favorites:talk',
	})

	React.useEffect(() => {
		if (!user) return
		if (talkFavoritesFetcher.data) return
		if (talkFavoritesFetcher.state !== 'idle') return
		void talkFavoritesFetcher.load(`${favoriteResourceRoute}?contentType=talk`)
	}, [user, talkFavoritesFetcher])

	const favoriteTalkIds = new Set(talkFavoritesFetcher.data?.contentIds ?? [])

	// An effect to scroll to the talk's position when opening a direct link,
	// use a ref so that it doesn't hijack scroll when the user is browsing talks
	React.useEffect(() => {
		document.querySelector(`[data-talk="${activeSlug}"]`)?.scrollIntoView()
	}, [activeSlug])

	// this bit is very similar to what's on the blogs page.
	// Next time we need to do work in here, let's make an abstraction for them
	const [searchParams] = useSearchParams()

	const [queryValue, setQuery] = React.useState<string>(() => {
		return searchParams.get('q') ?? ''
	})

	const talks = queryValue
		? data.talks.filter((talk) =>
				queryValue.split(',').every((tag) => talk.tags.includes(tag)),
			)
		: data.talks

	const visibleTags = new Set(talks.flatMap((x) => x.tags))

	function toggleTag(tag: string) {
		setQuery((q) => {
			const existing = q
				.split(',')
				.map((x) => x.trim())
				.filter(Boolean)

			const newQuery = existing.includes(tag)
				? existing.filter((t) => t !== tag)
				: [...existing, tag]

			return newQuery.join(',')
		})
	}

	useUpdateQueryStringValueWithoutNavigation('q', queryValue)

	return (
		<>
			<HeroSection
				title="Check out these talks."
				subtitle={
					<>
						Mostly on{' '}
						<a href="https://kcd.im/map" className="underline">
							location
						</a>
						, sometimes remote.
					</>
				}
				imageBuilder={images.teslaY}
				imageSize="large"
			/>

			<Grid className="mb-14">
				<H6 as="div" className="col-span-full mb-6">
					Search talks by topics
				</H6>
				<div className="col-span-full -mr-4 -mb-4 flex flex-wrap lg:col-span-10">
					{data.tags.map((tag) => (
						<Tag
							key={tag}
							tag={tag}
							selected={queryValue.includes(tag)}
							onClick={() => toggleTag(tag)}
							disabled={Boolean(
								!visibleTags.has(tag) && !queryValue.includes(tag),
							)}
						/>
					))}
				</div>
			</Grid>

			<Grid className="mb-64">
				{data.talks.length === 0 ? (
					<div className="col-span-full rounded-lg border border-gray-200 p-8 dark:border-gray-600">
						<H4 as="h2" className="mb-3">
							No talks are available right now.
						</H4>
						<Paragraph className="mb-4">
							We are likely having trouble with our GitHub integration. Please
							try again soon, or browse the content directly on{' '}
							<a
								href={externalLinks.githubRepo}
								target="_blank"
								rel="noreferrer noopener"
								className="text-primary underline"
							>
								GitHub
							</a>
							.
						</Paragraph>
						<ButtonLink variant="primary" to={externalLinks.githubRepo}>
							Open GitHub repo
						</ButtonLink>
					</div>
				) : (
					<>
						<H6 as="h2" className="col-span-full mb-6">
							{queryValue
								? talks.length === 1
									? `1 talk found`
									: `${talks.length} talks found`
								: 'Showing all talks'}
						</H6>

						<div className="col-span-full">
							<Grid nested rowGap>
								{talks.map((talk) => {
									return (
										<div
											key={talk.slug}
											className="col-span-full lg:col-span-6"
										>
											<Card
												active={activeSlug === talk.slug}
												isFavorite={favoriteTalkIds.has(talk.slug)}
												{...talk}
											/>
										</div>
									)
								})}
							</Grid>
						</div>
					</>
				)}
			</Grid>

			<CourseSection />
		</>
	)
}
